import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { admin } from "../lib/api";
import { date, money, ORDER_STATUS, PAYMENT_METHOD, PAYMENT_STATUS } from "../lib/format";
import type { List, OrderSummary } from "../lib/types";
import { Badge, Button, PageHeader, Spinner, Table } from "../components/ui";

const TABS = [["", "Toate"], ["pending", "Noi"], ["confirmed", "Confirmate"], ["processing", "În procesare"], ["shipped", "Expediate"], ["delivered", "Livrate"], ["cancelled", "Anulate"]];

export default function Orders() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const customer = params.get("customer_id") ?? "";
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");

  const query = useInfiniteQuery({
    queryKey: ["orders", { status, term, customer }],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: "50" });
      if (status) p.set("status", status);
      if (term) p.set("q", term);
      if (customer) p.set("customer_id", customer);
      if (pageParam) p.set("cursor", pageParam);
      return admin.get<List<OrderSummary>>(`/orders?${p}`);
    },
    getNextPageParam: (l) => l.next_cursor || undefined,
    refetchInterval: 30_000, // comenzile noi apar fără refresh manual
  });
  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <PageHeader title="Comenzi" subtitle={customer ? <>Filtrat după client · <button className="text-brand" onClick={() => setParams({})}>elimină filtrul</button></> : undefined} />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1 text-sm">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setParams(v ? { status: v } : {})}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 font-medium ${status === v ? "bg-white shadow-sm" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}>
          <input className="input w-64" placeholder="Nr. comandă sau email…" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
      </div>
      {query.isLoading ? <Spinner /> : (
        <Table head={["Comanda", "Data", "Client", "Status", "Plată", "Produse", "Total"]} empty={rows.length === 0}>
          {rows.map((o) => (
            <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/orders/${o.id}`)}>
              <td className="px-4 py-3 font-semibold">#{o.number}</td>
              <td className="px-4 py-3 text-slate-500">{date(o.created_at)}</td>
              <td className="px-4 py-3">{o.email}</td>
              <td className="px-4 py-3"><Badge tone={ORDER_STATUS[o.status]?.tone}>{ORDER_STATUS[o.status]?.label ?? o.status}</Badge></td>
              <td className="px-4 py-3">
                <Badge tone={PAYMENT_STATUS[o.payment_status]?.tone}>{PAYMENT_STATUS[o.payment_status]?.label}</Badge>
                <div className="mt-0.5 text-xs text-slate-400">{PAYMENT_METHOD[o.payment_method] ?? o.payment_method}</div>
              </td>
              <td className="px-4 py-3 text-slate-500">{o.item_count}</td>
              <td className="px-4 py-3 font-semibold">{money(o.total, o.currency)}</td>
            </tr>
          ))}
        </Table>
      )}
      {query.hasNextPage && (
        <div className="mt-4 text-center"><Button variant="secondary" onClick={() => query.fetchNextPage()}>Încarcă mai multe</Button></div>
      )}
    </>
  );
}
